// A Dashboard query answered whole and swapped in at once.
//
// The split's empties pane asks its own question (Alan, 2026-09-26: "这个空柜的页面
// 是和另外的页面是分开独立的就是不要让别的页面的筛选影响到空柜部分"), and the
// Dashboard's data controller is the wrong shape for it. That controller clears its
// rows the moment a new question is asked and appends each page as it lands, which
// is right for the main table - it sits under a loading curtain - and wrong for a
// pane that is supposed to stay still while the toolbar beside it changes. The
// server scans the fleet in document-id order, so an empty on the third page would
// arrive a second after one on the first: every refresh would blank the pane, then
// grow it back in steps.
//
// So this keeps what it has until the whole answer is in, then replaces it in one
// step. The page contract is the controller's: the same page size and the same
// three checks on every response (see dashboard-data-controller.mjs).

import { DASHBOARD_PAGE_LIMIT } from './dashboard-data-controller.mjs?v=20260806.100';

// 1677 Containers is three pages; forty is a runaway cursor, not a fleet.
const MAX_PAGES = 40;

export function createWholeQuerySource({ callList, normalizeRow, onChange }) {
  if (typeof callList !== 'function' || typeof normalizeRow !== 'function'
    || typeof onChange !== 'function') {
    throw new TypeError('Whole query source dependencies are invalid.');
  }

  let generation = 0;
  let rows = [];
  let query = null;
  let loading = false;
  let error = null;

  function snapshot() {
    return { rows: [...rows], loading, error, active: query !== null };
  }

  async function load(nextQuery, { seed = null } = {}) {
    generation += 1;
    const token = generation;
    query = nextQuery && typeof nextQuery === 'object' ? { ...nextQuery } : {};
    // What the pane was already showing, so starting to ask does not blank it.
    if (Array.isArray(seed)) rows = [...seed];
    loading = true;
    error = null;
    onChange(snapshot());
    try {
      const collected = new Map();
      let cursor = null;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const response = await callList({ ...query, limit: DASHBOARD_PAGE_LIMIT, cursor });
        if (token !== generation) return false;
        const data = response?.data;
        if (!Array.isArray(data?.items) || typeof data.hasMore !== 'boolean') {
          throw new TypeError('DASHBOARD_RESPONSE_INVALID');
        }
        for (const item of data.items) {
          const row = normalizeRow(item);
          if (row && typeof row.id === 'string' && row.id && !collected.has(row.id)) {
            collected.set(row.id, row);
          }
        }
        if (!data.hasMore) break;
        const nextCursor = typeof data.nextCursor === 'string' && data.nextCursor ? data.nextCursor : null;
        if (!nextCursor || nextCursor === cursor) throw new TypeError('DASHBOARD_PAGINATION_INVALID');
        cursor = nextCursor;
      }
      rows = [...collected.values()];
      loading = false;
      onChange(snapshot());
      return true;
    } catch (caught) {
      if (token !== generation) return false;
      // The rows it had stay on screen: a failed refresh is not an empty fleet.
      error = caught;
      loading = false;
      onChange(snapshot());
      throw caught;
    }
  }

  return Object.freeze({
    load,
    // The same question again, after a release, a cancellation or a refused save.
    refresh() {
      return query === null ? Promise.resolve(false) : load(query);
    },
    patchRow(id, patch) {
      if (typeof id !== 'string' || !id || !patch || typeof patch !== 'object'
        || Array.isArray(patch)) return false;
      const index = rows.findIndex((row) => row.id === id);
      if (index < 0) return false;
      rows = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
      onChange(snapshot());
      return true;
    },
    // A Container the operator moved into this source's pane by hand. The next
    // answer to the question replaces it like any other row.
    adoptRow(row) {
      if (query === null || !row || typeof row !== 'object' || typeof row.id !== 'string'
        || !row.id || rows.some(({ id }) => id === row.id)) return false;
      rows = [...rows, row];
      onChange(snapshot());
      return true;
    },
    snapshot,
    isActive: () => query !== null,
    clear() {
      generation += 1;
      rows = [];
      query = null;
      loading = false;
      error = null;
    },
  });
}
